import { useMemo, useState } from 'react';
import {
  headLabel,
  useBookingHeads,
  useCreateBookingHead,
  type BookingHeadCategory,
  type BookingHeadRow,
} from '@/lib/booking-heads';

type Props = {
  value: string;
  onChange: (v: string) => void;
  category: BookingHeadCategory;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
};

const ADD_NEW = '__ADD_NEW__';

export function BookingHeadSelect({
  value,
  onChange,
  category,
  disabled,
  className,
  ariaLabel = 'Booking head',
}: Props) {
  const heads = useBookingHeads();
  const create = useCreateBookingHead();
  const [creating, setCreating] = useState(false);

  const options: BookingHeadRow[] = useMemo(() => {
    const list = (heads.data ?? []).filter((h) => h.category === category);
    list.sort((a, b) => a.label.localeCompare(b.label));
    return list;
  }, [heads.data, category]);

  async function handleAddNew() {
    const label = window.prompt(
      category === 'CLIENT_BOOKING'
        ? 'New booking-head name (e.g. "Airport transfer")'
        : 'New office-expense head (e.g. "EMI payment")',
    );
    if (!label || !label.trim()) return;
    setCreating(true);
    try {
      const row = await create.mutateAsync({ label: label.trim(), category });
      onChange(row.key);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to add head');
    } finally {
      setCreating(false);
    }
  }

  return (
    <select
      aria-label={ariaLabel}
      value={value || ''}
      disabled={disabled || heads.isLoading || creating}
      onChange={(e) => {
        const v = e.target.value;
        if (v === ADD_NEW) {
          e.preventDefault();
          void handleAddNew();
          return;
        }
        onChange(v);
      }}
      className={
        className ??
        'w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 focus:border-neutral-500 focus:outline-none dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100'
      }
    >
      <option value="">— Select head —</option>
      {options.map((h) => (
        <option key={h.key} value={h.key}>
          {h.label}
        </option>
      ))}
      <option value={ADD_NEW}>+ Add new head…</option>
    </select>
  );
}

export function useHeadLabel() {
  const heads = useBookingHeads();
  return (key: string | null | undefined) => headLabel(key, heads.data);
}
